import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { Text, Button, Link } from '@/ui'
import { TypographyStyles } from '@/styles'

import {
  activateCancelledAccount,
  DOWNLOAD_ACCOUNT_ZIP_KEY,
  downloadAccountInformation,
} from '@/actions/userActions'
import { getCurrentUser } from '@/selectors/user.selectors'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { AccountDownloadDialog } from './AccountDownloadDialog'

export const CanceledAccount = () => {
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const navigate = useNavigate()
  const currentUser = useReselector(getCurrentUser)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const downloadingFiles = useReselector(selectIsFetchingForKeys, [
    DOWNLOAD_ACCOUNT_ZIP_KEY,
  ])

  useEffect(() => {
    pageView('account-canceled')
  }, [pageView])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
      setFetching(false)
    }

    fetch()
  }, [dispatch])

  const downloadZipFile = async () => {
    track('download account zip file — canceled already')

    const result = await downloadAccountInformation(currentUser?.id)(dispatch)
    if (result) {
      setDownloadModalOpen(true)
    }
  }

  /**
   * User is reactivating a canceled account.
   *
   * We:
   * - Call canceled_account_reactivation endpoint, which marks the user as reactivated
   * - Navigate user to sign up flow with a reactivation param (user needs to re-sign up)
   */
  const canceledToReactivate = async () => {
    const result = await activateCancelledAccount(currentUser?.id)(dispatch)
    // reactivated, nav to sign up
    if (result) {
      sendUserCancellationMessageZap(
        currentUser,
        'reactivated_cancelled_account'
      )
      navigate('/signup/about-your-practice')
    }
  }

  return (
    <>
      <div className="w-full px-4 py-6">
        {fetching && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="text-white text-lg">Loading account</div>
          </div>
        )}

        <div className="flex flex-col items-center space-y-6">
          <div className="w-full max-w-3xl text-center">
            <Text variant={TypographyStyles.H1}>
              You&apos;ve canceled your Heard account
            </Text>
          </div>

          <div className="w-full max-w-3xl text-center">
            <Text variant={TypographyStyles.BODY_MD}>
              You&apos;re unable to use Heard without a paid account. Don&apos;t
              worry, you&apos;ll still be able to download your financial
              information and documents if needed.
            </Text>
          </div>

          <div className="w-full max-w-3xl flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-1/2">
              <Button
                variant="primary"
                className="w-full"
                onClick={canceledToReactivate}
              >
                Reactivate Account
              </Button>
            </div>
            <div className="w-full md:w-1/2">
              <Button
                variant="secondary"
                className="w-full"
                onClick={downloadZipFile}
                loading={downloadingFiles}
                disabled={downloadingFiles}
              >
                Download Account Information
              </Button>
            </div>
          </div>

          <div className="w-full max-w-md">
            <Text>
              Is this a mistake? Contact us via email at{' '}
              <Link
                href="mailto:support@joinheard.com"
                variant="secondary"
                newPage
              >
                support@joinheard.com
              </Link>
              .
            </Text>
          </div>
        </div>
      </div>

      <AccountDownloadDialog
        open={downloadModalOpen}
        close={() => setDownloadModalOpen(false)}
      />
    </>
  )
}
